/**
 * @file RateTableLibraryDrawer.jsx
 * @module ui/src/components/tenant-admin/sections/RateTableLibraryDrawer
 * @description
 *   Slide-in drawer for browsing and selecting a specific governed rate table
 *   version to pin against a plan document. Filters by spine compatibility
 *   and approved-only status by default. Shows a band preview and reference
 *   count for the selected table before confirming. The plan remains pinned
 *   to the explicitly chosen version until an intentional change is made —
 *   auto-latest binding is strictly prohibited (Locked Decision #29).
 *
 * @version 1.0.1
 * @since S754
 * @author data-fit LLC · DecisionFlow · S775
 *
 * @architecture
 *   Locked decisions : #10, #15, #25, #29
 *   Temporal invariants : INV-3, INV-9
 *   Design principles : DP-5, DP-14
 *   Cross-cutting contracts : XC-7
 *
 * @domain-rules
 *   - The plan pins to a specific rate table version; auto-latest resolution
 *     is never performed. The user must explicitly select a version.
 *   - Rate table IDs are never exposed to the user; only name and version
 *     label are shown (DP-5: no internal keys in UI).
 *   - Compatibility filter defaults ON so incompatible tables require an
 *     intentional override to inspect.
 *   - Approved-only filter defaults ON; unapproved tables are accessible
 *     only when the admin explicitly turns the filter off.
 *   - The selection model is a single-select toggle; the native ARIA pattern
 *     for toggle buttons (aria-pressed) is used, not listbox/option, because
 *     each result is a native <button> element (OI-S754-C16-ARIA-LISTBOX-01).
 *
 * @session-6-migration-flags
 *   - rate_table.effective_from / effective_to — pending m_tc_06; component
 *     normalizes both camelCase and snake_case field names defensively.
 *   - GET /api/v1/rate-tables endpoint contract — Session 6 API gap.
 *
 * @changelog
 *   1.0.0  S754  Initial build — Group B batch delivery
 *   1.0.1  S756  OI-S754-C16-ARIA-LISTBOX-01 — removed role="listbox" from
 *                result container and role="option"/aria-selected from result
 *                buttons. role="option" is invalid on a <button> element.
 *                Replaced with aria-pressed={selected} on each result button,
 *                which correctly communicates toggle selection state for
 *                native button elements per ARIA authoring practices.
 *                Full data-fit production file standard applied (S756).
 */

import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

// WHY: Focusable selector is defined once for the focus-trap useEffect.
const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// Map canonical primarySpine keys to human-readable labels for display
const SPINE_LABELS = {
  revenue_bookings:       'Revenue / Bookings',
  transaction_commission: 'Transaction Commission',
  volume_quantity:        'Volume / Quantity',
  market_share:           'Market Share / Penetration',
  grr_nrr_retention:      'Retention (GRR / NRR)',
  mbo_objective:          'MBO / Objective',
  margin_profit:          'Margin / Profit',
  bonus_pool:             'Bonus Pool / Allocation',
  pharma_script:          'Pharma / Script Volume',
};

function spineLabel(spine) {
  if (!spine) return null;
  return SPINE_LABELS[spine] ?? spine;
}

/**
 * WHY: formatScope presents the effective window in plain language because
 * the temporal contract requires explicit date ranges, not implicit "current".
 */
function formatScope(from, to) {
  if (!from && !to) return "No effective scope supplied";
  if (from && !to) return `${from} onward`;
  if (!from && to) return `Through ${to}`;
  return `${from} – ${to}`;
}

/**
 * WHY: formatBoundary renders a null ceiling as "No ceiling" rather than
 * an empty cell so the user knows the absence is intentional.
 */
function formatBoundary(value) {
  return value == null ? "No ceiling" : `${value}%`;
}

/**
 * WHY: normalizeResults maps both camelCase and snake_case API response
 * shapes to a single internal shape, preventing field-name drift from
 * causing silent undefined renders as the API is finalized in Session 6.
 * Handles both { rateTables: [...] } and { items: [...] } envelope shapes.
 */
function normalizeResults(payload) {
  const source = Array.isArray(payload)
    ? payload
    : payload?.rateTables ?? payload?.items ?? payload?.rate_tables ?? [];
  return source.map((item) => ({
    rateTableId:    item.rateTableId    ?? item.rate_table_id,
    rateTableCode:  item.rateTableCode  ?? item.rate_table_code  ?? '',
    rateTableName:  item.rateTableName  ?? item.rate_table_name  ?? '',
    versionLabel:   item.versionLabel   ?? item.version_label    ?? `V${item.version ?? 1}`,
    effectiveFrom:  item.effectiveFrom  ?? item.effective_from   ?? null,
    effectiveTo:    item.effectiveTo    ?? item.effective_to     ?? null,
    spineCompatible: item.spineCompatible ?? item.spine_compatible ?? false,
    compatibleSpines: item.compatibleSpines ?? item.compatible_spines ?? [],
    otherPlanCount: item.otherPlanCount ?? item.other_plan_count ?? 0,
    bands: (item.bands ?? item.band_preview ?? []).map((band) => ({
      from: band.from ?? band.attainment_from ?? band.from_pct,
      to:   band.to   ?? band.attainment_to   ?? band.to_pct   ?? null,
      rate: band.rate ?? band.payout_rate     ?? band.payout_pct,
    })),
  }));
}

/**
 * @typedef {Object} RateTableRef
 * @property {number} rateTableId
 * @property {string} rateTableName
 * @property {string} versionLabel
 */

/**
 * @typedef {Object} RateTableLibraryDrawerProps
 * @property {boolean} open
 * @property {RateTableRef|null} currentRef  - The currently pinned rate table reference.
 * @property {string} planSpine              - The plan's measurement spine for compatibility filtering.
 * @property {Function} onSelect             - Called with {rateTableId, rateTableName, versionLabel} on confirm.
 * @property {Function} onClose
 */

/**
 * Rate table library drawer for explicit version selection.
 *
 * @param {RateTableLibraryDrawerProps} props
 * @returns {JSX.Element|null}
 */
export default function RateTableLibraryDrawer({
  open,
  currentRef,
  planSpine,
  onSelect,
  onClose,
  onCreateRateTable,
}) {
  const titleId = useId();
  const descriptionId = useId();
  const drawerRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  // WHY: All filter state is local presentation state; the component never
  // holds or infers any plan document state.
  const [searchQuery, setSearchQuery] = useState("");
  const [compatibilityFilterOn, setCompatibilityFilterOn] = useState(true);
  const [approvedOnlyFilterOn, setApprovedOnlyFilterOn] = useState(true);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);

  // WHY: loadTables is memoized on its filter inputs so the debounced
  // useEffect only re-fetches when the user changes search or filter state.
  // The AbortController in the effect cancels in-flight requests on re-run.
  const loadTables = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      params.set("approved_only", String(approvedOnlyFilterOn));
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      if (compatibilityFilterOn && planSpine) {
        params.set("spine", planSpine);
      }

      try {
        const response = await fetch(
          `/api/v1/rate-tables?${params.toString()}`,
          { signal, credentials: 'include' }
        );

        if (!response.ok) {
          throw new Error(`Unable to load rate tables (${response.status}).`);
        }

        const payload = await response.json();
        setResults(normalizeResults(payload));
      } catch (fetchError) {
        if (fetchError.name !== "AbortError") {
          setResults([]);
          setError(
            fetchError.message || "Unable to load the rate table library."
          );
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [approvedOnlyFilterOn, compatibilityFilterOn, planSpine, searchQuery]
  );

  // WHY: Debounced fetch — 200ms prevents rapid filter changes from firing
  // multiple concurrent requests. AbortController cancels on re-run or close.
  useEffect(() => {
    if (!open) return undefined;

    const controller = new AbortController();
    const timer = window.setTimeout(
      () => loadTables(controller.signal),
      200
    );

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, loadTables]);

  // WHY: Focus trap and focus restoration. selectedPreview is cleared on
  // close so re-opening the drawer starts with a clean selection state.
  useEffect(() => {
    if (!open) {
      setSelectedPreview(null);
      return undefined;
    }

    previousFocusRef.current = document.activeElement;
    window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) return;

      const focusable = Array.from(
        drawerRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
      );

      if (!focusable.length) {
        event.preventDefault();
        drawerRef.current.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const target = previousFocusRef.current;
      if (target instanceof HTMLElement) target.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const noCompatibleTables =
    !loading &&
    !error &&
    compatibilityFilterOn &&
    results.length === 0 &&
    !searchQuery.trim();

  // WHY: handleConfirm passes the full normalised rate table object so that
  // RateTableReference can display the band preview, effective scope, and deal cap
  // without a second fetch. The plan document pin uses rateTableId; the other
  // fields are display-only and never written to the plan record directly.
  const handleConfirm = () => {
    if (!selectedPreview) return;
    onSelect(selectedPreview);
  };

  return (
    <div
      className="rate-table-library-overlay"
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgb(var(--color-text) / 0.28)",
        zIndex: 50,
      }}
    >
      <section
        ref={drawerRef}
        className="modifier-manager-card rate-table-library-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        style={{
          position: "absolute",
          insetBlock: 0,
          insetInlineEnd: 0,
          width: "min(760px, 100%)",
          background: "rgb(var(--color-surface))",
          borderInlineStart: "1px solid rgb(var(--color-border))",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header className="modifier-manager-header">
          <div>
            <h2 id={titleId}>Choose a rate table</h2>
            <p id={descriptionId}>
              Select a specific governed version. The plan will remain
              pinned to that version until an explicit change is made.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="icon-button"
            aria-label="Close rate table library"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="modifier-manager-body">
          <label>
            <span>Search rate tables</span>
            <input
              type="search"
              value={searchQuery}
              placeholder="Search by rate table name"
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setSelectedPreview(null);
              }}
            />
          </label>

          <div
            role="group"
            aria-label="Rate table filters"
            style={{ display: "flex", gap: 16, flexWrap: "wrap" }}
          >
            <label>
              <input
                type="checkbox"
                checked={compatibilityFilterOn}
                onChange={(event) => {
                  setCompatibilityFilterOn(event.target.checked);
                  setSelectedPreview(null);
                }}
              />
              Compatible with {spineLabel(planSpine) ?? "selected spine"} only
            </label>

            <label>
              <input
                type="checkbox"
                checked={approvedOnlyFilterOn}
                onChange={(event) => {
                  setApprovedOnlyFilterOn(event.target.checked);
                  setSelectedPreview(null);
                }}
              />
              Current approved versions only
            </label>
          </div>

          {currentRef && (
            <div className="modifier-manager-info">
              Current reference: <strong>{currentRef.rateTableName}</strong>{" "}
              {currentRef.versionLabel}
            </div>
          )}

          {error && (
            <div className="modifier-manager-error-state" role="alert">
              <strong>Rate table library unavailable</strong>
              <p>{error}</p>
            </div>
          )}

          {loading && (
            <div aria-label="Loading rate tables" aria-busy="true">
              {[0, 1, 2, 3].map((row) => (
                <div
                  className="skeleton"
                  key={row}
                  style={{ height: 56, marginBlockEnd: 8 }}
                />
              ))}
            </div>
          )}

          {noCompatibleTables && (
            <div className="modifier-manager-empty-state">
              <strong>
                No rate tables found{planSpine ? ` for ${spineLabel(planSpine)}` : ''}.
              </strong>
              <p>
                No approved rate tables exist yet. You can create one with
                sensible defaults below, or import rate table shells and rows
                via Import Data → RT Shells / Rate Table Rows.
              </p>
              {onCreateRateTable && (
                <button
                  type="button"
                  className="primary-button"
                  style={{ marginTop: 12 }}
                  onClick={() => onCreateRateTable({
                    planSpine,
                    // Sensible defaults: standard attainment curve
                    // 5 bands: 0-50% pays 0%, 50-80% pays 50%,
                    // 80-100% pays 80%, 100-120% pays 110%, 120%+ pays 130%
                    defaultRows: [
                      { from: 0,   to: 50,   payout: 0,   interpolation: 'linear' },
                      { from: 50,  to: 80,   payout: 50,  interpolation: 'linear' },
                      { from: 80,  to: 100,  payout: 80,  interpolation: 'linear' },
                      { from: 100, to: 120,  payout: 110, interpolation: 'linear' },
                      { from: 120, to: null, payout: 130, interpolation: 'linear' },
                    ],
                  })}
                >
                  Create rate table with defaults
                </button>
              )}
              <button
                type="button"
                className="text-button"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setCompatibilityFilterOn(false);
                  setApprovedOnlyFilterOn(false);
                }}
              >
                Show all rate tables (remove filters)
              </button>
            </div>
          )}

          {!loading && !error && !noCompatibleTables && results.length === 0 && (
            <div className="modifier-manager-empty-state">
              <strong>No rate tables match these filters.</strong>
              <p>
                Adjust the search text, compatibility filter, or
                approved-version filter.
              </p>
            </div>
          )}

          {!loading && results.length > 0 && (
            /*
             * WHY: This is a plain div, NOT role="listbox". Each result is a
             * native <button> element; role="option" is only valid as a child
             * of role="listbox" and only on non-interactive elements. Using
             * role="option" on a <button> creates an invalid ARIA ownership
             * relationship and is a production accessibility defect.
             * The correct pattern for a group of toggle buttons is
             * aria-pressed on each <button>. (OI-S754-C16-ARIA-LISTBOX-01)
             */
            <div
              aria-label="Rate tables"
              className="rate-table-library-results"
            >
              {results.map((table) => {
                const selected =
                  selectedPreview?.rateTableId === table.rateTableId;

                return (
                  // WHY: aria-pressed communicates the selected/deselected
                  // toggle state for a native button element. data-selected
                  // is retained for any CSS that targets it.
                  <button
                    type="button"
                    aria-pressed={selected}
                    data-selected={selected ? "true" : "false"}
                    className="secondary-button"
                    key={`${table.rateTableId}:${table.versionLabel}`}
                    onClick={() => setSelectedPreview(table)}
                    style={{
                      width: "100%",
                      textAlign: "start",
                      marginBlockEnd: 8,
                    }}
                  >
                    <strong>{table.rateTableName}</strong>
                    <span> · {table.versionLabel}</span>
                    <span>
                      {" "}
                      ·{" "}
                      {table.spineCompatible
                        ? `Compatible with ${spineLabel(planSpine) ?? "selected spine"}`
                        : `Not compatible with ${planSpine}`}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {selectedPreview && (
            <section
              className="modifier-manager-card"
              aria-labelledby={`${titleId}-preview`}
            >
              <div className="modifier-manager-header">
                <div>
                  <h3 id={`${titleId}-preview`}>
                    {selectedPreview.rateTableName}{" "}
                    {selectedPreview.versionLabel}
                  </h3>
                  <p>
                    {formatScope(
                      selectedPreview.effectiveFrom,
                      selectedPreview.effectiveTo
                    )}
                  </p>
                </div>
                {/* WHY: Compatibility state is named in text; badge class
                     reinforces it visually (XC-7/DP-14). */}
                <span
                  className={
                    selectedPreview.spineCompatible
                      ? "modifier-manager-badge--complete"
                      : "modifier-manager-badge--incomplete"
                  }
                >
                  {selectedPreview.spineCompatible
                    ? `Compatible with ${spineLabel(planSpine) ?? "selected spine"}`
                    : `Not compatible with ${planSpine}`}
                </span>
              </div>

              <div className="modifier-manager-body">
                <div className="modifier-manager-table-wrapper">
                  <table className="modifier-manager-table">
                    <caption className="sr-only">
                      Preview of the first rate table bands
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">From</th>
                        <th scope="col">To</th>
                        <th scope="col">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPreview.bands.slice(0, 4).map((band, index) => (
                        <tr key={`${band.from}:${band.to}:${index}`}>
                          <td>{formatBoundary(band.from)}</td>
                          <td>{formatBoundary(band.to)}</td>
                          <td>{band.rate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p>
                  Referenced by{" "}
                  <strong>{selectedPreview.otherPlanCount}</strong>{" "}
                  other{" "}
                  {selectedPreview.otherPlanCount === 1 ? "plan" : "plans"}.
                </p>
              </div>

              <footer className="modifier-manager-footer">
                {/* WHY: Confirm is disabled for incompatible tables so the
                     admin cannot accidentally pin a table the engine cannot
                     resolve against this spine. */}
                <button
                  type="button"
                  className="primary-button"
                  disabled={!selectedPreview.spineCompatible}
                  onClick={handleConfirm}
                >
                  Use this rate table
                </button>
              </footer>
            </section>
          )}
        </div>
      </section>
    </div>
  );
}

/*
 * DOMAIN COMPLIANCE CHECK
 * - Locked decision #29: Auto-latest binding is prohibited. The user must
 *   explicitly select and confirm a specific version. No "latest approved"
 *   shortcut exists in this component.
 * - Locked decision #10: Rate tables retain shared object identity; this
 *   component selects a reference, not a copy.
 * - DP-5: Rate table IDs are never exposed; only name and version label shown.
 * - DP-14/XC-7: Compatibility state is always named in text; badge class
 *   reinforces visually but never defines the state.
 * - OI-S754-C16-ARIA-LISTBOX-01: role="listbox"/"option"/aria-selected removed.
 *   aria-pressed={selected} on each result button is the correct ARIA pattern
 *   for toggle selection on native button elements.
 */

/*
 * SESSION 6 MIGRATION FLAGS
 * - rate_table.effective_from / effective_to — pending m_tc_06.
 * - GET /api/v1/rate-tables endpoint contract — Session 6 API gap; component
 *   normalizes both camelCase and snake_case field names defensively.
 */

/*
 * ACCESSIBILITY NOTES
 * - Drawer is a dialog with aria-modal="true", aria-labelledby, aria-describedby.
 * - Focus trap cycles all focusable elements; initial focus on close button.
 * - On close: focus returns to the element that opened the drawer.
 * - Escape closes the drawer from any focused element within it.
 * - Result buttons use aria-pressed (not role="option") for toggle selection.
 * - Loading skeletons carry aria-busy="true" on their container.
 * - Error states use role="alert".
 * - Table has a visually hidden caption for screen reader context.
 * - Compatibility badges communicate state as text; color is reinforcement only.
 */
